import { FunctionComponent, useCallback, useId, useMemo } from 'react';
import styles from './styles.module.scss';
import Card from '../../Card';
import Button from '../../Button';
import { MinusSolidIcon, PlusSolidIcon } from '../../Icons';
import { LocalDate } from '../../LocalDate';
import { Temporal } from 'temporal-polyfill';
import { renderPrice } from '../../../services/renderPrice';

export type QuantityCardProps = {
    name: string;
    price?: number;
    description?: string;
    quantity: number;
    maximumQuantity?: number;
    hidePricing?: boolean;
    end_at?: number;
    start_at?: number;
    onChange: (newQuantity: number) => unknown;
    currency: string;
};

export const QuantityCard: FunctionComponent<QuantityCardProps> = ({
    name,
    price,
    description,
    quantity,
    maximumQuantity,
    hidePricing,
    start_at,
    end_at,
    onChange,
    currency,
}) => {
    const id = useId();
    const now = Date.now() / 1000;

    const isAdditionDisabled = useMemo(() => {
        if (maximumQuantity === undefined) {
            return false;
        }
        if (start_at && now < start_at) return true;
        if (end_at && now > end_at) return true;
        return quantity >= maximumQuantity;
    }, [quantity, maximumQuantity, start_at, end_at, now]);

    const isSubtractionDisabled = useMemo(() => {
        return quantity === 0;
    }, [quantity]);

    const displayPrice = useMemo(() => {
        if (price === undefined) return null;
        if (hidePricing) return null;
        const priceLabel = renderPrice(price, currency);
        return <span className={styles.price}>{priceLabel}</span>;
    }, [price, hidePricing, currency]);

    const addQuantity = useCallback(() => {
        onChange(quantity + 1);
    }, [quantity, onChange]);

    const subtractQuantity = useCallback(() => {
        onChange(quantity - 1);
    }, [quantity, onChange]);

    return (
        <Card id={id} className={styles.container}>
            <div className={styles.headerBar}>
                <span id={`${id}_name`} className={styles.name}>
                    {name}
                </span>
                <div>{displayPrice}</div>
                <div className={styles.controls}>
                    <Button
                        className={styles.button}
                        aria-label="Remove one from basket"
                        onClick={subtractQuantity}
                        disabled={isSubtractionDisabled}
                    >
                        <MinusSolidIcon />
                    </Button>
                    <div
                        role="spinbutton"
                        tabIndex={0}
                        aria-labelledby={`${id}_name`}
                        aria-valuenow={quantity}
                        aria-valuemin={0}
                        aria-valuemax={maximumQuantity}
                    >
                        {quantity}
                    </div>
                    <Button
                        preset="primary"
                        className={styles.button}
                        aria-label="Add one to basket"
                        onClick={addQuantity}
                        disabled={isAdditionDisabled}
                    >
                        <PlusSolidIcon />
                    </Button>
                </div>
            </div>
            {start_at && now < start_at ? (
                <p className={styles.saleDate}>
                    Sales start on{' '}
                    {
                        <LocalDate
                            date={Temporal.Instant.fromEpochSeconds(start_at)}
                            format={{
                                day: 'numeric',
                                month: 'short',
                                hour: 'numeric',
                                minute: 'numeric',
                            }}
                        />
                    }
                </p>
            ) : null}
            {end_at && now > end_at ? (
                <p className={styles.saleDate}>
                    Sales ended on{' '}
                    {
                        <LocalDate
                            date={Temporal.Instant.fromEpochSeconds(end_at)}
                            format={{
                                day: 'numeric',
                                month: 'short',
                                hour: 'numeric',
                                minute: 'numeric',
                            }}
                        />
                    }
                </p>
            ) : null}
            <p className={styles.description}>{description}</p>
        </Card>
    );
};
